import React from 'react';
import { useTranslation } from 'react-i18next';
import { useBranding } from 'modules/App/hooks/useBranding';
import { supportLinePrefix } from 'modules/Auth/config/clerkLocalization';
import { darkSurfaceAccent } from 'modules/Common/utils/brandColor';
import { CgCitedText } from 'modules/Common/ui/CgLegalCitation';
import WorldTradeIllustration from './WorldTradeIllustration';

// Split auth layout shared by /sign-in and /sign-up: one continuous dark
// brand backdrop across the whole page — the animated world-trade
// illustration on the left (~70% on desktop) and the Clerk card column on the
// right share the same panel color, so the two halves read as one surface
// instead of hard-divided sections. On <lg the illustration disappears and
// the card floats on the same dark backdrop.
//
// The logo lives INSIDE the Clerk card (clerkAppearance logoPlacement
// 'inside'), not on the column: brand logos — ours included — are drawn for
// light backgrounds, and the card is the only guaranteed-light surface.
//
// Clerk's own card header stays visible (not hidden via appearance): in the
// combined withSignUp flow it is the only copy that tracks the active step
// (sign-in vs sign-up vs verification), so a static heading here would lie.
//
// The backdrop is a fixed ink-navy — deliberately NOT brand-derived. The
// brand color appears in exactly two places in the scene (the animated trade
// flows and the EU/UK border in WorldTradeIllustration) plus the links, so a
// tenant's primary reads as a deliberate accent against a constant surround.
// Mixing the brand into the backdrop (an earlier iteration) gave every
// tenant a different surround hue, which shifted the globe's perceived
// colors per tenant (simultaneous contrast) and could never be tuned once.
// The navy sits in the same hue family (~210°) as the illustration's sea, so
// globe and panel read as one continuous night-ocean scene.
const PANEL_BG = '#0b141d';

const CheckIcon = ({ accent }) => (
  <svg
    className="mt-0.5 h-4 w-4 shrink-0 text-primaryColor"
    style={{ color: accent }}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <circle
      cx="8"
      cy="8"
      r="7"
      stroke="currentColor"
      strokeOpacity="0.45"
    />
    <path
      d="M4.8 8.2l2.1 2.1 4.3-4.6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AuthShell = ({ children, footer }) => {
  const { t } = useTranslation();
  const brand = useBranding();
  // Near-black brand primaries (e.g. Crowe navy) disappear as accents against
  // the dark panel — those brands swap to their dark-surface accent, same rule
  // as the chart tooltips (resolvedPrimaryColorOnDark).
  const accent = darkSurfaceAccent(brand.key);

  const heroPoints = [
    t('auth.heroPointEu', 'Built for EU CBAM — Regulation (EU) 2023/956'),
    t('auth.heroPointUk', 'Ready for UK CBAM — Finance Act 2026, in force from 2027'),
    t('auth.heroPointPlatform', 'One platform for manufacturers, distributors, and importers'),
  ];

  return (
    <div
      className="min-h-screen w-full flex"
      style={{ backgroundColor: PANEL_BG }}
    >
      <div className="relative hidden lg:flex flex-1 overflow-hidden text-white">
        <WorldTradeIllustration accent={accent} />
        <div
          className="absolute inset-x-0 bottom-0 h-2/5"
          style={{ background: `linear-gradient(to top, ${PANEL_BG}, transparent)` }}
          aria-hidden="true"
        />
        <div className="relative z-10 flex w-full flex-col justify-end p-10 xl:p-14">
          <div className="max-w-xl">
            <h1 className="text-3xl xl:text-4xl font-semibold leading-tight">
              {t('auth.heroTitle', 'Make your business CBAM-proof')}
            </h1>
            <p className="mt-4 text-base xl:text-lg text-white/70">
              {t(
                'auth.heroSubtitle',
                'Collect supplier data, get ready for verification, track CBAM costs, and file reports you can stand behind.',
              )}
            </p>
            <ul className="mt-8 space-y-3">
              {heroPoints.map((point) => (
                <li
                  key={point}
                  className="flex items-start gap-3 text-sm text-white/80"
                >
                  <CheckIcon accent={accent} />
                  <span>
                    <CgCitedText
                      text={point}
                      linkClassName="text-white decoration-white/50 hover:decoration-white"
                    />
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <p className="mt-10 text-xs text-white/40">{`© ${new Date().getFullYear()} ${brand.name}`}</p>
        </div>
      </div>

      <div className="flex w-full shrink-0 flex-col lg:w-[440px] xl:w-[500px]">
        <div className="flex flex-1 flex-col items-center justify-center px-4 py-10 sm:px-8">
          <div className="flex w-full flex-col items-center">{children}</div>
          {brand.supportEmail && (
            <p className="mt-6 text-center text-sm text-white/60">
              {supportLinePrefix()}{' '}
              <a
                href={`mailto:${brand.supportEmail}`}
                className="font-medium text-primaryColor hover:underline"
                style={{ color: accent }}
              >
                {brand.supportEmail}
              </a>
            </p>
          )}
          {footer}
        </div>
      </div>
    </div>
  );
};

export default AuthShell;
